import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ListTodo, Plus, AlertTriangle, ChevronDown, Check, X, RotateCcw } from 'lucide-react';
import { safeFetch } from '../../../lib/api';
import { notifyToast } from '../../../lib/toastStore';
import type { InternalTask } from './InternalTasksSection';

interface DrawerTasksSectionProps {
  /** RESOLVED application document id — the linkage key tasks carry. */
  applicationId: string;
  /** Bumped by the drawer on every (re)load; refetch when it changes. */
  refreshSignal?: number;
  /** Opens the drawer's create-task modal (the section's one small
   *  add-task control lives in this header). */
  onAddTask: () => void;
  /** Notify the drawer after a mutation so sibling sections refresh. */
  onChanged?: () => void;
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Linked internal tasks for THIS applicant, inside the drawer.
 *
 * Reads and writes go through the SAME endpoints the Internal Tasks
 * page uses (GET list with applicationId + status=all; PATCH for
 * transitions) — no parallel write path. The list endpoint merges
 * automation-engine tasks (different stored shape) into the same
 * response with origin attribution, so tasks created in the drawer,
 * on the Tasks page, or by an automation all appear here. Grouping
 * mirrors the Tasks page rules: open tasks first; a task whose
 * applicant moved on stays open with its "moved" badge; completing
 * requires closing notes (enforced server-side too).
 */
export const DrawerTasksSection: React.FC<DrawerTasksSectionProps> = ({
  applicationId,
  refreshSignal,
  onAddTask,
  onChanged,
}) => {
  const [tasks, setTasks] = useState<InternalTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showClosed, setShowClosed] = useState(false);
  // Inline transition forms — one open at a time.
  const [completeId, setCompleteId] = useState<string | null>(null);
  const [closingNotes, setClosingNotes] = useState('');
  const [cancelId, setCancelId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('status', 'all');
      params.set('applicationId', applicationId);
      const data: any = await safeFetch(`/api/admin/workflow/internal-tasks?${params.toString()}`);
      setTasks(data.tasks || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [applicationId]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const lastSignalRef = useRef(refreshSignal ?? 0);
  useEffect(() => {
    const s = refreshSignal ?? 0;
    if (s !== lastSignalRef.current) {
      lastSignalRef.current = s;
      fetchTasks();
    }
  }, [refreshSignal, fetchTasks]);

  const setStatus = async (task: InternalTask, status: 'pending' | 'completed' | 'canceled') => {
    if (status === 'completed' && !closingNotes.trim()) {
      notifyToast('warning', 'Closing notes are required to complete a task');
      return;
    }
    setBusyId(task.id);
    try {
      await safeFetch(`/api/admin/workflow/internal-tasks/${task.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status,
          ...(status === 'completed' ? { closingNotes: closingNotes.trim() } : {}),
        }),
      });
      notifyToast('success', `Task marked ${status}`);
      setCompleteId(null);
      setCancelId(null);
      setClosingNotes('');
      await fetchTasks();
      onChanged?.();
    } catch (err: any) {
      notifyToast('error', err.message || 'Update failed');
    } finally {
      setBusyId(null);
    }
  };

  const open = tasks.filter((t) => t.status === 'pending');
  const closed = tasks.filter((t) => t.status !== 'pending');

  const renderTask = (t: InternalTask) => {
    const isAutomation = t.source === 'automation';
    return (
      <li key={t.id} className="px-4 py-2.5">
        <div className="flex items-start gap-2.5">
          <span
            className={`mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${
              t.status === 'completed'
                ? 'bg-emerald-500 text-white'
                : t.status === 'canceled'
                  ? 'bg-slate-300 text-slate-600'
                  : 'bg-amber-400 text-white'
            }`}
            aria-hidden="true"
          >
            {t.status === 'completed' ? '✓' : t.status === 'canceled' ? '×' : '!'}
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[13px] font-semibold text-slate-900 leading-snug">{t.details}</p>
            <p className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-1 text-[11px] text-slate-500">
              {isAutomation ? (
                <span className="text-[10px] font-extrabold uppercase tracking-wide text-violet-700 bg-violet-50 border border-violet-200 px-1.5 py-0.5 rounded">
                  Automation
                </span>
              ) : (
                <span className="uppercase tracking-wide font-bold text-slate-400">
                  {t.stageStampLabel || t.stageStamp}
                </span>
              )}
              {t.aiSuggested && (
                <span className="text-[10px] font-extrabold text-violet-600 bg-violet-50 px-1.5 py-0.5 rounded">AI</span>
              )}
              {t.assigneeName && <span>→ {t.assigneeName}</span>}
              {t.completeBy && t.status === 'pending' && <span>due {fmtDate(t.completeBy)}</span>}
              <span className="text-slate-400">
                by {t.createdByName}
                {t.createdAt ? ` · ${fmtDate(t.createdAt)}` : ''}
              </span>
            </p>
            {t.status === 'completed' && (
              <p className="text-[11px] text-emerald-700 mt-0.5">
                Completed
                {t.completedAt ? ` ${fmtDate(t.completedAt)}` : ''}
                {t.completedByName ? ` by ${t.completedByName}` : ''}
                {t.closingNotes ? <span className="block text-slate-600">“{t.closingNotes}”</span> : null}
              </p>
            )}
            {t.movedOn && (
              <span className="inline-block mt-1 text-[10px] font-extrabold text-amber-800 bg-amber-100 px-2 py-0.5 rounded-full">
                Applicant moved to {t.currentStageLabel} — review
              </span>
            )}
            {t.underlyingResolved && t.status === 'pending' && (
              <span className="inline-block mt-1 ml-1 text-[10px] font-extrabold text-emerald-800 bg-emerald-100 px-2 py-0.5 rounded-full">
                Underlying item looks resolved — safe to close?
              </span>
            )}

            {/* Inline transitions */}
            {t.status === 'pending' && completeId !== t.id && cancelId !== t.id && (
              <div className="mt-1.5 flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => { setCompleteId(t.id); setClosingNotes(''); }}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-success hover:bg-[color-mix(in_srgb,var(--color-success)_90%,#000)] text-white text-[11px] font-bold cursor-pointer"
                >
                  <Check className="h-3 w-3" /> Complete
                </button>
                <button
                  type="button"
                  onClick={() => setCancelId(t.id)}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-slate-200 text-slate-600 text-[11px] font-bold hover:bg-slate-50 cursor-pointer"
                >
                  <X className="h-3 w-3" /> Cancel
                </button>
              </div>
            )}
            {t.status === 'pending' && completeId === t.id && (
              <div className="mt-2 space-y-1.5">
                <textarea
                  value={closingNotes}
                  onChange={(e) => setClosingNotes(e.target.value)}
                  rows={2}
                  placeholder="Closing notes (required) — what was done…"
                  className="w-full border border-slate-200 rounded-xl px-3 py-2 text-xs text-slate-800 resize-y"
                  autoFocus
                />
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => setStatus(t, 'completed')}
                    disabled={busyId === t.id}
                    className="px-3 py-1.5 rounded-lg bg-success hover:bg-[color-mix(in_srgb,var(--color-success)_90%,#000)] disabled:opacity-50 text-white text-[11px] font-bold cursor-pointer"
                  >
                    {busyId === t.id ? 'Saving…' : 'Confirm complete'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setCompleteId(null)}
                    disabled={busyId === t.id}
                    className="px-2.5 py-1.5 rounded-lg border border-slate-200 text-slate-600 text-[11px] font-bold hover:bg-slate-50 disabled:opacity-50 cursor-pointer"
                  >
                    Back
                  </button>
                </div>
              </div>
            )}
            {t.status === 'pending' && cancelId === t.id && (
              <div className="mt-2 flex items-center gap-1.5">
                <span className="text-[11px] font-bold text-slate-600">Cancel this task?</span>
                <button
                  type="button"
                  onClick={() => setStatus(t, 'canceled')}
                  disabled={busyId === t.id}
                  className="px-3 py-1.5 rounded-lg bg-slate-700 hover:bg-slate-600 disabled:opacity-50 text-white text-[11px] font-bold cursor-pointer"
                >
                  {busyId === t.id ? 'Saving…' : 'Yes, cancel'}
                </button>
                <button
                  type="button"
                  onClick={() => setCancelId(null)}
                  disabled={busyId === t.id}
                  className="px-2.5 py-1.5 rounded-lg border border-slate-200 text-slate-600 text-[11px] font-bold hover:bg-slate-50 disabled:opacity-50 cursor-pointer"
                >
                  Keep
                </button>
              </div>
            )}
            {t.status !== 'pending' && (
              <div className="mt-1.5">
                <button
                  type="button"
                  onClick={() => setStatus(t, 'pending')}
                  disabled={busyId === t.id}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-amber-200 text-amber-800 text-[11px] font-bold hover:bg-amber-50 disabled:opacity-50 cursor-pointer"
                >
                  <RotateCcw className="h-3 w-3" /> Reopen
                </button>
              </div>
            )}
          </div>
        </div>
      </li>
    );
  };

  return (
    <div className="bg-white border border-slate-200/80 rounded-2xl shadow-2xs overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100">
        <ListTodo className="h-4 w-4 text-amber-600" />
        <h3 className="text-sm font-extrabold text-slate-900 tracking-tight">
          Tasks{' '}
          <span className="text-slate-400 font-bold tabular-nums">
            ({open.length} open{closed.length > 0 ? ` · ${closed.length} closed` : ''})
          </span>
        </h3>
        <button
          type="button"
          onClick={onAddTask}
          className="ml-auto inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-brand hover:bg-brand-active text-white text-[11px] font-bold cursor-pointer shrink-0"
        >
          <Plus className="h-3.5 w-3.5" /> Add task
        </button>
      </div>

      {loading && tasks.length === 0 ? (
        <div className="p-4 space-y-2" aria-busy="true">
          {[0, 1].map((i) => (
            <div key={i} className="h-12 bg-slate-100 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : error ? (
        <div className="px-4 py-3 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-red-400 shrink-0" />
          <p className="text-xs font-bold text-slate-600 flex-1">{error}</p>
          <button type="button" onClick={fetchTasks} className="text-xs font-bold text-indigo-600 underline cursor-pointer">
            Retry
          </button>
        </div>
      ) : tasks.length === 0 ? (
        <p className="px-4 py-4 text-xs font-semibold text-slate-500">
          No tasks linked to this applicant yet.
        </p>
      ) : (
        <>
          {open.length > 0 ? (
            <ul className="divide-y divide-slate-100">{open.map(renderTask)}</ul>
          ) : (
            <p className="px-4 py-3 text-xs font-semibold text-slate-500">No open tasks — all clear.</p>
          )}
          {closed.length > 0 && (
            <div className="border-t border-slate-100">
              <button
                type="button"
                onClick={() => setShowClosed((v) => !v)}
                aria-expanded={showClosed}
                className="w-full flex items-center gap-2 px-4 py-2.5 text-[11px] font-bold text-slate-500 hover:bg-slate-50/60 cursor-pointer"
              >
                Closed tasks ({closed.length})
                <ChevronDown className={`h-3.5 w-3.5 ml-auto transition-transform ${showClosed ? '' : '-rotate-90'}`} />
              </button>
              {showClosed && <ul className="divide-y divide-slate-100">{closed.map(renderTask)}</ul>}
            </div>
          )}
        </>
      )}
    </div>
  );
};
